import React, { useState, useEffect, useRef } from 'react';
import './EvidentAnimation.css';

const DOC_LINES    = ['Age ≥ 18 yrs', 'Limit $500k'];
const REVIEW_ITEMS = ['Age rule', 'Limit rule', 'Income check'];

const DoneCheck = () => (
  <svg className="ax-done-icon" width="13" height="13" viewBox="0 0 14 14" fill="none"
    style={{ animation: 'axCheckIn 0.35s ease both' }}>
    <circle cx="7" cy="7" r="6.5" fill="#ecfdf5" stroke="#6ee7b7" strokeWidth="1"/>
    <path d="M3.5 7l2.5 2.5 4.5-4.5" stroke="#059669" strokeWidth="1.4"
      strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const MiniCheck = () => (
  <svg width="8" height="8" viewBox="0 0 10 10" fill="none"
    style={{ animation: 'axCheckIn 0.3s cubic-bezier(0.34,1.56,0.64,1) both' }}>
    <path d="M1.5 5l2.5 2.5 5-5" stroke="#fff" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const EvidentAnimation = () => {
  const [cycleKey, setCycleKey] = useState(0);
  const [cols, setCols]         = useState(['idle', 'idle', 'idle']); // idle | active | done

  // Encode sub-state
  const [scanLine, setScanLine]           = useState(0);
  const [showExtracted, setShowExtracted] = useState(false);
  // Verify sub-state
  const [approvedIdx, setApprovedIdx] = useState(0);
  // Evolve sub-state
  const [showUserMsg, setShowUserMsg]   = useState(false);
  const [showReply, setShowReply]       = useState(false);
  const [showDeploy, setShowDeploy]     = useState(false);

  const timers = useRef([]);
  const clr  = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  const wait = (fn, ms) => { const t = setTimeout(fn, ms); timers.current.push(t); };

  useEffect(() => {
    clr();
    setCols(['idle', 'idle', 'idle']);
    setScanLine(0); setShowExtracted(false);
    setApprovedIdx(0);
    setShowUserMsg(false); setShowReply(false); setShowDeploy(false);

    const c = (i, s) => setCols(p => p.map((v, j) => j === i ? s : v));

    // ENCODE (0 – 3.6s): scan doc lines one by one, then show extracted result
    wait(() => c(0, 'active'),          400);
    wait(() => setScanLine(1),          1000);
    wait(() => setScanLine(2),          1900);
    wait(() => setShowExtracted(true),  2800);

    // VERIFY (3.7 – 7.5s): expert approves items one by one
    const V = 3700;
    wait(() => { c(0, 'done'); c(1, 'active'); }, V);
    wait(() => setApprovedIdx(1), V + 600);
    wait(() => setApprovedIdx(2), V + 1300);
    wait(() => setApprovedIdx(3), V + 2000);

    // EVOLVE (7.7 – 12.3s): user chat → AI reply → deploy confirmation
    const E = V + 4000;
    wait(() => { c(1, 'done'); c(2, 'active'); }, E);
    wait(() => setShowUserMsg(true),  E + 700);
    wait(() => setShowReply(true),    E + 1900);
    wait(() => setShowDeploy(true),   E + 2900);
    wait(() => c(2, 'done'),          E + 3700);

    // CYCLE
    wait(() => setCycleKey(k => k + 1), E + 5200);
    return clr;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleKey]);

  return (
    <div className="ax-wrap">
      <div className="ax-card">
        <div className="ax-cols">

          {/* ── Col 1: Encode — scan document lines ── */}
          <div className={`ax-col ax-col--${cols[0]}`}>
            <div className="ax-col-hdr">
              <div className={`ax-badge ax-badge--${cols[0]}`}>1</div>
              <span className="ax-col-title">Encode</span>
              {cols[0] === 'done' && <DoneCheck/>}
            </div>
            <div className="ax-col-body">
              <div className="ax-doc">
                <div className="ax-doc-filename">
                  <svg width="10" height="10" viewBox="0 0 12 14" fill="none">
                    <path d="M2 0h6l4 4v9a1 1 0 01-1 1H2a1 1 0 01-1-1V1a1 1 0 011-1z"
                      fill="#e5e7eb" stroke="#d1d5db" strokeWidth="0.5"/>
                    <path d="M7 0v4h4" fill="none" stroke="#d1d5db" strokeWidth="0.5"/>
                  </svg>
                  <span>policy.pdf</span>
                </div>
                {DOC_LINES.map((line, i) => (
                  <div key={i} className={`ax-doc-line ${i < scanLine ? 'ax-doc-line--scanned' : ''}`}>
                    <span className="ax-doc-txt">{line}</span>
                    {i < scanLine && (
                      <svg className="ax-scan-check" width="8" height="8" viewBox="0 0 10 10" fill="none"
                        style={{ animation: 'axCheckIn 0.25s ease both' }}>
                        <path d="M1.5 5l2.5 2.5 5-5" stroke="#4338ca" strokeWidth="1.8"
                          strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                ))}
              </div>
              {showExtracted && (
                <div className="ax-extracted" style={{ animation: 'axSlideUp 0.3s ease both' }}>
                  <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                    <path d="M1 5l2.5 2.5 5.5-5.5" stroke="#059669" strokeWidth="1.8"
                      strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  2 rules encoded
                </div>
              )}
            </div>
          </div>

          <div className="ax-divider"/>

          {/* ── Col 2: Verify — expert approves items ── */}
          <div className={`ax-col ax-col--${cols[1]}`}>
            <div className="ax-col-hdr">
              <div className={`ax-badge ax-badge--${cols[1]}`}>2</div>
              <span className="ax-col-title">Verify</span>
              {cols[1] === 'done' && <DoneCheck/>}
            </div>
            <div className="ax-col-body">
              <div className="ax-expert-row">
                <div className="ax-expert-av">SM</div>
                <span className="ax-expert-name">Expert review</span>
              </div>
              {REVIEW_ITEMS.map((item, i) => {
                const approved = i < approvedIdx;
                return (
                  <div key={i} className={`ax-review-item ${approved ? 'ax-review-item--approved' : ''}`}>
                    <div className={`ax-review-circle ${approved ? 'ax-review-circle--done' : ''}`}>
                      {approved && <MiniCheck/>}
                    </div>
                    <span className="ax-review-lbl">{item}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="ax-divider"/>

          {/* ── Col 3: Evolve — conversational rule update ── */}
          <div className={`ax-col ax-col--${cols[2]}`}>
            <div className="ax-col-hdr">
              <div className={`ax-badge ax-badge--${cols[2]}`}>3</div>
              <span className="ax-col-title">Evolve</span>
              {cols[2] === 'done' && <DoneCheck/>}
            </div>
            <div className="ax-col-body">
              {!showUserMsg && (
                <div className="ax-placeholder">
                  <div className="ax-ph-dot"/><div className="ax-ph-dot"/><div className="ax-ph-dot"/>
                </div>
              )}
              {showUserMsg && (
                <div className="ax-chat-pill" style={{ animation: 'axSlideUp 0.3s ease both' }}>
                  <div className="ax-avatar"/>
                  <span className="ax-chat-txt">"Raise age to 21"</span>
                </div>
              )}
              {showReply && (
                <div className="ax-chat-reply" style={{ animation: 'axSlideUp 0.3s ease both' }}>
                  <span className="ax-reply-txt">age ≥ 21 ✓</span>
                </div>
              )}
              {showDeploy && (
                <div className="ax-result" style={{ animation: 'axSlideUp 0.3s ease both' }}>
                  <div className="ax-live-dot"/>
                  <span>Deployed · Live</span>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default EvidentAnimation;
